import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { saveAs } from 'file-saver';
import { isEmpty } from 'lodash';
import { NzTableSortOrder } from 'ng-zorro-antd/table';
import { TrafficConfig } from 'src/app/core';
import {
  ByteUnit,
  Order,
  ResourceMode,
  TrafficLabelId,
} from 'src/app/core/enums';
import { SubscriptionStatus } from 'src/app/core/proto/shopkeeper/subscriptions/enums/subscription-status.enum';
import { PlanRecurrenceType } from 'src/app/core/proto/reconciliator/plans/enums/plan-recurrence-type.enum';
import { NetworkMnoSubscription } from 'src/app/core/proto/reconciliator/plans/interfaces/network-mno-subscriptions.interface';
import { PaymentChannelType } from 'src/app/core/proto/shopkeeper/common/enums/payment-channel-type.enum';
import { TranslationService } from 'src/app/core/services/translation.service';
import { DataControlType } from 'src/app/dashboard/mobile/plans/enums';
import { DefaultConst } from '../constants';
import { SortOrder } from '../enums/pagination';
import { BrandIntegrationType } from '../proto/brandassador/brands/enums/brand-integration-type.enum';
import { BrandDetails } from '../proto/brandassador/brands/interfaces/brand-details.interface';
import { TrafficSpeed } from '../proto/reconciliator/plans/enums/plan-traffic-speed.enum';
import { TransactionReason } from 'src/app/dashboard/mobile/end-users/enums/transaction-reason.enum';
import {
  NotificationSmsStep,
  NotificationSmsStepNotification,
  SmsMacro,
} from 'src/app/dashboard/brand-settings/notifications/interfaces';
import { SmsTriggerEvent } from 'src/app/dashboard/brand-settings/notifications/enums';
import moment from 'moment';
import { NotificationTrigger } from 'src/app/core/proto';
import { string } from 'yargs';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  paginationDefaultOptions: number[] = DefaultConst.PAGINATION_LIST;

  constructor(private translationService: TranslationService) {}

  static calculateDigit(curp: string): string {
    const sqRoot = curp.substring(0, 17);
    const chrCaracter = '0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ';
    const intFactor = new Array(17);
    let lngSum = 0.0;
    let lngDigit = 0.0;

    for (let i = 0; i < 17; i++) {
      for (let j = 0; j < 37; j++) {
        if (sqRoot.substring(i, i + 1) === chrCaracter.substring(j, j + 1)) {
          intFactor[i] = j;
        }
      }
    }

    for (let k = 0; k < 17; k++) {
      lngSum = lngSum + intFactor[k] * (18 - k);
    }

    lngDigit = 10 - (lngSum % 10);

    if (lngDigit === 10) {
      lngDigit = 0;
    }

    return lngDigit.toString();
  }

  convertSecToMs(sec: number): number {
    return sec * 1000;
  }

  convertMinToSec(minutes: number): number {
    return minutes * 60;
  }

  convertSecToMin(seconds: number): number {
    return seconds / 60;
  }

  convertDayToMs(days: number): number {
    return days * 86400000;
  }

  convertDayToSec(days: number): number {
    return days * 86400;
  }

  convertSecToDay(seconds: number): number {
    return seconds / 86400;
  }

  convertKibByte(kib: number): number {
    return kib * 1024;
  }

  convertKibToGib(kib: number): number {
    return kib / 1048576;
  }

  convertGibToKib(gb: number): number {
    return gb * 1048576;
  }

  convertKibToMib(kib: number): number {
    return kib / 1024;
  }

  convertMibToKib(mb: number): number {
    return mb * 1024;
  }

  convertMibToGib(mb: number): number {
    return mb / 1024;
  }

  convertGibToMib(gb: number): number {
    return gb * 1024;
  }

  convertToCents(amount: number) {
    return amount * 100;
  }

  convertToDollars(amount: number) {
    return amount / 100;
  }

  mathCeil(amount: number) {
    return Math.ceil(amount);
  }

  /**
   * Convert value based on given byte units list
   * @param value
   * @param units list of available byte units
   * @returns
   */
  convertKibBasedOnByteUnits(
    value: number,
    units: string[] = Object.keys(ByteUnit)
  ): { value: number; unit: ByteUnit } {
    let convertedValue = value;
    const capacityUnit = this.getByteUnitFromKib(value, units);
    switch (capacityUnit) {
      case ByteUnit.MB:
        convertedValue = this.convertKibToMib(value);
        break;
      case ByteUnit.GB:
        convertedValue = this.convertKibToGib(value);
        break;
      // case ByteUnit.TB:
      //   convertedValue = this.convertKibToTib(value);
      //   break;
      default: // ByteUnit.KB
        break;
    }

    return {
      value: convertedValue,
      unit: capacityUnit,
    };
  }

  scrollDrawerUp() {
    let top = document.getElementsByClassName('ant-drawer-body');
    if (top.length) {
      top[0].scroll(0, 0);
    }
  }

  downloadCSVFile(data: any, fileName: string) {
    const replacer = (key, value) => (value === null ? '' : value); // specify how you want to handle null values here
    const header = Object.keys(data[0]);
    let csv = data.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    csv.unshift(header.join(','));
    let csvArray = csv.join('\r\n');

    const blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, `${fileName}.csv`);
  }

  /**
   * Update sort order
   * @param sortBy
   * @param actualSortBy
   * @param orderBy
   */
  updateSortOrder(
    sortBy: any,
    actualSortBy: any,
    orderBy: NzTableSortOrder | null
  ) {
    if (actualSortBy !== sortBy && !orderBy) {
      return;
    } else {
      return {
        sortBy: sortBy,
        orderBy: orderBy,
      };
    }
  }

  getPaginationList(totalItemsCount: number) {
    let paginationList = [];
    let maxPaginationNumberReached = false;
    for (let defaultOption of this.paginationDefaultOptions) {
      if (!maxPaginationNumberReached) {
        paginationList.push(defaultOption);
        maxPaginationNumberReached = defaultOption >= totalItemsCount;
      }
    }

    return paginationList;
  }

  /**
   * Get sorting order
   * @param orderBy
   */
  getTableSortOrder(orderBy: NzTableSortOrder | null) {
    if (orderBy === 'ascend') return SortOrder.SORT_ORDER_ASC;

    return SortOrder.SORT_ORDER_DESC;
  }

  getTableOrder(orderBy: NzTableSortOrder): Order | null {
    switch (orderBy) {
      case 'ascend':
        return Order.ASC;
      case 'descend':
        return Order.DESC;
      default:
        return null;
    }
  }

  createAdminRes(res: string): string {
    const pageTypes = ['portal3.tab', 'portal3.page'];
    let replacePageTypes;
    pageTypes.every((pt) => {
      if (res.includes(pt)) {
        replacePageTypes = pt;
        return false;
      }

      replacePageTypes = pt;
      return true;
    });

    return res.replace(replacePageTypes, 'portal3.admin');
  }

  /**
   * Filter out the test brands
   * @param brands
   * @returns
   */
  filterOutTestBrands(brands: BrandDetails[]): BrandDetails[] {
    return brands.filter((brand) => {
      if (brand.displayName.startsWith('ACCEPTANCE_')) return false;

      if (!Object.values(BrandIntegrationType).includes(brand.integrationType))
        return false;

      return true;
    });
  }

  getByteUnitFromKib(
    usage: number,
    units: string[] = Object.keys(ByteUnit) // List of available units
  ): ByteUnit {
    if (!usage) return;

    let byte: ByteUnit = null;
    usage = usage * 1024;

    if (units.includes(ByteUnit.TB) && usage >= 1024 ** 4) {
      byte = ByteUnit.TB;
    }

    if (
      (!units.includes(ByteUnit.TB) && usage >= 1024 ** 4) ||
      (units.includes(ByteUnit.GB) && usage < 1024 ** 4)
    ) {
      byte = ByteUnit.GB;
    }

    if (units.includes(ByteUnit.MB) && usage < 1024 ** 3) {
      byte = ByteUnit.MB;
    }

    if (units.includes(ByteUnit.KB) && usage < 1024 ** 2) {
      byte = ByteUnit.KB;
    }

    return byte;
  }

  filterTrafficLabels(trafficLabels: TrafficConfig[]): any[] {
    if (trafficLabels && trafficLabels.length > 0) {
      const isNoTrafficLabelSelected = [...trafficLabels].every(
        (trafficLabel) =>
          trafficLabel.trafficLabelId === TrafficLabelId.GENERAL &&
          trafficLabel.speed === TrafficSpeed.SPEED_MAX
      );

      if (isNoTrafficLabelSelected) {
        return [];
      }

      // GENERAL trafficLabels need to be filtered out if mixed with other trafficLabels
      // to return the right length
      return [...trafficLabels].filter(
        (trafficLabel) =>
          !(
            trafficLabel.trafficLabelId === TrafficLabelId.GENERAL &&
            trafficLabel.speed === TrafficSpeed.SPEED_MAX
          )
      );
    }

    return [];
  }

  /**
   * Filter traffic labels by their speed (Dedicated Data vs Low Speed Data)
   * @param trafficLabels
   * @param dataControlType
   * @returns
   */
  filterTrafficLabelsBySpeed(
    trafficLabels: TrafficConfig[],
    dataControlType: DataControlType
  ): TrafficConfig[] {
    const speed =
      dataControlType === DataControlType.DEDICATED_DATA
        ? TrafficSpeed.SPEED_MAX
        : TrafficSpeed.SPEED_512KBPS;

    if (trafficLabels && trafficLabels.length > 0) {
      return trafficLabels.filter(
        (trafficLabel) => trafficLabel.speed === speed
      );
    }

    return trafficLabels;
  }

  checkIfFieldIsRequired(fieldName: string, form: FormGroup): boolean {
    const control = form.get(fieldName);

    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      return validator && validator.required;
    }

    return false;
  }

  getMnoSubscriptionById(
    mnoSubscriptionUuid: string,
    mnoSubscriptions: NetworkMnoSubscription[]
  ) {
    if (mnoSubscriptionUuid) {
      const mnoSubscription = mnoSubscriptions.find(
        (mnoSubscription) =>
          mnoSubscription.mnoSubscriptionUuid === mnoSubscriptionUuid
      );

      if (mnoSubscription) {
        return mnoSubscription.displayName;
      }
    }

    return null;
  }

  setPaymentChannelsLogosUrl() {
    return {
      SELLER_APP: '/assets/images/logos/payment-channels/SELLER_APP.svg',
      API_SALE: '/assets/images/logos/payment-channels/API_SALE.svg',
      PORTAL: '/assets/images/logos/payment-channels/PORTAL.svg',
      OPEN_PAY: '/assets/images/logos/payment-channels/OPEN_PAY.svg',
      OXXO_PAY: '/assets/images/logos/payment-channels/OXXO_PAY.svg',
      REWARD: '/assets/images/logos/payment-channels/REWARD.svg',
      POINT_BANK: '/assets/images/logos/payment-channels/POINT_BANK.svg',
      VESTA_CARD: '/assets/images/logos/payment-channels/VESTA_CARD.svg',
    };
  }

  removePropFromObj(obj: any, propertyName: any): any {
    let { [propertyName]: _, ...result } = obj;
    return result;
  }

  arrayDiff(a1, a2) {
    var a = [],
      diff = [];
    for (var i = 0; i < a1.length; i++) {
      a[a1[i]] = true;
    }
    for (var i = 0; i < a2.length; i++) {
      if (a[a2[i]]) {
        delete a[a2[i]];
      } else {
        a[a2[i]] = true;
      }
    }
    for (var k in a) {
      diff.push(k);
    }
    return diff;
  }

  /**
   * Permissions
   * @param displayName
   * @returns
   */
  getCategoryNameFromScopeName(displayName: string): string {
    return displayName && displayName.length > 0
      ? displayName.split(':')[0]
      : '';
  }

  groupBy<T, K extends keyof any>(
    list: T[],
    getKey: (item: T) => K
  ): Record<K, T[]> {
    return list.reduce((previous, currentItem) => {
      const group = getKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    }, {} as Record<K, T[]>);
  }

  /**
   * Eg.: Get "administrative:usersRoles:roles" from "administrative:usersRoles:roles:admin"
   * @param displayName
   * @returns
   */
  removeResModeFromScopeName(displayName: string): string {
    const resourceModes = Object.values(ResourceMode)
      .map((res) => res.toLowerCase())
      .join('|');
    const regex = new RegExp(':(' + resourceModes + ')$');
    return displayName.replace(regex, '');
  }

  /**
   * Display domains in a readable way
   * @param dom
   * @returns
   */
  permissionDomain(dom: string): string {
    if (dom === '[]') return '';
    return dom.replace(/[\[\]]/g, '').replace(/,/g, ', ');
  }

  /**
   * Get cursor coordinates on input/textarea element
   * {@link https://stackoverflow.com/a/7948715}
   * @param input
   * @param selectionStart
   * @param selectionEnd
   * @param debug
   * @returns
   */
  getTextBoundingRect(
    input: any,
    selectionStart: number,
    selectionEnd: number,
    debug: boolean = false
  ): {
    x: number;
    y: number;
    top: number;
    right: number;
    left: number;
    bottom: number;
    width: number;
    height: number;
  } {
    // Basic parameter validation
    if (!input || !('value' in input)) return input;
    if (typeof selectionStart == 'string')
      selectionStart = parseFloat(selectionStart);
    if (typeof selectionStart != 'number' || isNaN(selectionStart)) {
      selectionStart = 0;
    }
    if (selectionStart < 0) selectionStart = 0;
    else selectionStart = Math.min(input.value.length, selectionStart);
    if (typeof selectionEnd == 'string')
      selectionEnd = parseFloat(selectionEnd);
    if (
      typeof selectionEnd != 'number' ||
      isNaN(selectionEnd) ||
      selectionEnd < selectionStart
    ) {
      selectionEnd = selectionStart;
    }
    if (selectionEnd < 0) selectionEnd = 0;
    else selectionEnd = Math.min(input.value.length, selectionEnd);

    // If available (thus IE), use the createTextRange method
    if (typeof input.createTextRange == 'function') {
      var range = input.createTextRange();
      range.collapse(true);
      range.moveStart('character', selectionStart);
      range.moveEnd('character', selectionEnd - selectionStart);
      return range.getBoundingClientRect();
    }
    // createTextRange is not supported, create a fake text range
    var offset = getInputOffset(),
      topPos = offset.top,
      leftPos = offset.left,
      width = getInputCSS('width', true),
      height = getInputCSS('height', true);

    // Styles to simulate a node in an input field
    var cssDefaultStyles = 'white-space:pre;padding:0;margin:0;',
      listOfModifiers = [
        'direction',
        'font-family',
        'font-size',
        'font-size-adjust',
        'font-variant',
        'font-weight',
        'font-style',
        'letter-spacing',
        'line-height',
        'text-align',
        'text-indent',
        'text-transform',
        'word-wrap',
        'word-spacing',
      ];

    topPos += getInputCSS('padding-top', true) as number;
    topPos += getInputCSS('border-top-width', true) as number;
    leftPos += getInputCSS('padding-left', true) as number;
    leftPos += getInputCSS('border-left-width', true) as number;
    leftPos += 1; //Seems to be necessary

    for (var i = 0; i < listOfModifiers.length; i++) {
      var property = listOfModifiers[i];
      cssDefaultStyles += property + ':' + getInputCSS(property, false) + ';';
    }
    // End of CSS variable checks

    var text = input.value,
      textLen = text.length,
      fakeClone = document.createElement('div');
    if (selectionStart > 0) appendPart(0, selectionStart);
    var fakeRange = appendPart(selectionStart, selectionEnd);
    if (textLen > selectionEnd) appendPart(selectionEnd, textLen);

    // Styles to inherit the font styles of the element
    fakeClone.style.cssText = cssDefaultStyles;

    // Styles to position the text node at the desired position
    fakeClone.style.position = 'fixed';
    // fakeClone.style.zIndex = '9999'; // Uncomment when debugging on modals
    fakeClone.style.top = topPos + 'px';
    fakeClone.style.left = leftPos + 'px';
    fakeClone.style.width = width + 'px';
    fakeClone.style.height = height + 'px';
    fakeClone.style.backgroundColor = 'red';
    document.body.appendChild(fakeClone);
    var returnValue = fakeRange.getBoundingClientRect(); // Get rect

    if (!debug) fakeClone.parentNode.removeChild(fakeClone); // Remove temp
    return returnValue;

    // Local functions for readability of the previous code
    function appendPart(start: number, end: number) {
      var span = document.createElement('span');
      // Force styles to prevent unexpected results
      span.style.cssText = `${cssDefaultStyles}white-space:initial;`;
      span.textContent = text.substring(start, end);
      fakeClone.appendChild(span);
      return span;
    }
    // Computing offset position
    function getInputOffset() {
      var body = document.body,
        win = document.defaultView,
        docElem = document.documentElement,
        box = document.createElement('div');
      box.style.paddingLeft = box.style.width = '1px';
      body.appendChild(box);
      var isBoxModel = box.offsetWidth == 2;
      body.removeChild(box);
      box = input.getBoundingClientRect();
      var clientTop = docElem.clientTop || body.clientTop || 0,
        clientLeft = docElem.clientLeft || body.clientLeft || 0,
        scrollTop =
          win.pageYOffset ||
          (isBoxModel && docElem.scrollTop) ||
          body.scrollTop,
        scrollLeft =
          win.pageXOffset ||
          (isBoxModel && docElem.scrollLeft) ||
          body.scrollLeft;
      return {
        top: box['top'] + scrollTop - clientTop,
        left: box['left'] + scrollLeft - clientLeft,
      };
    }
    function getInputCSS(prop, isnumber: boolean) {
      var val = document.defaultView
        .getComputedStyle(input, null)
        .getPropertyValue(prop);
      return isnumber ? parseFloat(val) : val;
    }
  }

  //////////////////////////////////////////////////
  // Notifications
  //////////////////////////////////////////////////
  /**
   * Convert API values before displaying in portal
   * Eg.: From {{consumerIdentity.msisdn}} to 000-000-0000
   * @param sms
   * @param macros
   * @returns converted sms
   */
  convertSmsTokenToDummyValue(sms: string, macros: SmsMacro): string {
    if (!sms) return;
    Object.values(macros).forEach((macro) => {
      if (sms.includes(macro.token)) {
        const regex = new RegExp(`\\${macro.token}(?!(\\w))`, 'g');
        sms = sms.replace(regex, macro.dummyValue);
      }
    });
    return sms;
  }

  /**
   * Convert API values before displaying in the notification form
   * Eg.: From {{consumerIdentity.msisdn}} to $enduserphonenumber
   * @param sms
   * @param macros
   * @returns converted sms
   */
  convertSmsTokenToMacro(sms: string, macros: SmsMacro): string {
    if (!sms) return;
    Object.values(macros).forEach((macro) => {
      if (sms.includes(macro.token)) {
        const regex = new RegExp(`\\${macro.token}(?!(\\w))`, 'g');
        sms = sms.replace(regex, macro.macro);
      }
    });
    return sms;
  }

  /**
   * Convert macros to dummy values as user type in the notification form
   * Eg.: From $enduserphonenumber to 000-000-0000
   * @param sms
   * @param smsMacrosInfo
   * @param availableMacros
   * @param percentThresholdValue
   * @param isNotificationTriggerTemplate
   * @returns converted sms
   */
  convertSmsMacrosToDummyValues(
    sms: string,
    smsMacrosInfo: SmsMacro,
    availableMacros: SmsMacro,
    percentThresholdValue?: number,
    isNotificationTriggerTemplate?: boolean
  ): string {
    if (!sms) return;
    let dummySms = sms;
    if (!isEmpty(availableMacros)) {
      const dataThresholdPercentageMacro =
        smsMacrosInfo.DATA_THRESHOLD_PERCENTAGE.macro;
      const timeThresholdPercentageMacro =
        smsMacrosInfo.TIME_THRESHOLD_PERCENTAGE.macro;

      Object.entries(availableMacros).forEach((macro) => {
        if (isNotificationTriggerTemplate) {
          const regex = new RegExp(
            `(\\${dataThresholdPercentageMacro}|\\${timeThresholdPercentageMacro})`,
            'g'
          );

          if (percentThresholdValue) {
            dummySms = dummySms.replace(regex, `${percentThresholdValue}%`);
          }
        }
        if (macro[1].dummyValue) {
          const regex = new RegExp(`\\${macro[1].macro}(?!(\\w))`, 'g');
          dummySms = dummySms.replace(regex, macro[1].dummyValue);
        }
      });
    }
    return dummySms;
  }

  /**
   * Convert sms before sending to API
   * Eg.: From $enduserphonenumber to {{consumerIdentity.msisdn}}
   * @param sms
   * @param macros
   * @returns converted sms
   */
  convertSmsMacroToToken(sms: string, macros: Partial<SmsMacro>): string {
    if (!sms) return;
    Object.values(macros).forEach((macro) => {
      if (sms.includes(macro.macro)) {
        const regex = new RegExp(`\\${macro.macro}(?!(\\w))`, 'g');
        sms = sms.replace(regex, macro.token);
      }
    });
    return sms;
  }

  /**
   * Highlight SMS step in the page
   * @param notification
   * @param smsNotificationsLanguage
   * @returns
   */
  isSmsNotificationActive(
    notification: NotificationSmsStepNotification,
    smsNotificationsLanguage: string
  ): boolean {
    if (notification?.notificationDef) {
      return notification?.notificationDef?.locale === smsNotificationsLanguage;
    }

    if (notification?.triggers && notification?.triggers.length > 0) {
      return notification?.triggers.some((trigger) =>
        trigger.notificationTriggers.some(
          (notificationTrigger) =>
            notificationTrigger.message?.SMS?.locale[smsNotificationsLanguage]
        )
      );
    }

    return false;
  }

  /**
   * Make SMS step clickable to view the notification modal
   * @param brandId
   * @param isOxioAdmin
   * @param step
   * @returns
   */
  enableViewNotification(
    brandId: string,
    isOxioAdmin: boolean,
    step: NotificationSmsStep
  ): boolean {
    // Consumption and time reminders both need the brandId to be sent to API
    if (
      isOxioAdmin &&
      !brandId &&
      (step.triggerEventType === SmsTriggerEvent.PLAN_CONSUMPTION_REMINDER ||
        step.triggerEventType === SmsTriggerEvent.PLAN_TIME_REMINDER)
    ) {
      return false;
    }

    return !!step.triggerEventType;
  }

  /**
   * Display the number of SMS per threshold on each threshold card in the
   * notification modal
   * @param notificationTriggers
   * @param language
   * @returns
   */
  smsNotificationsPerThresholdCount(
    notificationTriggers: NotificationTrigger[],
    language: string
  ): number {
    const sms = []
      .concat(
        ...notificationTriggers.map(
          (notificationTrigger) =>
            notificationTrigger.message.SMS.locale[language]
        )
      )
      .filter((sms) => typeof sms !== 'undefined');

    if (sms.length > 0) return sms.length;

    return 0;
  }

  isThresholdSMSExists(sms: string): boolean {
    return typeof sms !== 'undefined';
  }

  isThresholdSMSSetInOtherLanguages(notificationTriggerMessageSMSLocale: {
    [key: string]: any;
  }): boolean {
    return Object.keys(notificationTriggerMessageSMSLocale).length > 1;
  }

  //////////////////////////////////////////////////
  // Plans
  //////////////////////////////////////////////////

  /**
   * Get transaction reason from memo value
   * @param memo
   * @returns
   */
  getTransactionReason(memo: string): string {
    const transactionReasonsExclOther: string[] = Object.keys(
      TransactionReason
    ).filter(
      (transactionReason) => transactionReason !== TransactionReason.OTHER
    );
    if (transactionReasonsExclOther.includes(memo)) {
      return this.translationService.instant(
        'GLOBAL_TERMS.TRANSACTION_REASON.' + memo
      );
    }
    return memo;
  }

  /**
   * @description How often the subscription will renew, determined by the plan duration
   * @param {number} duration plan's duration
   * @param {PlanRecurrenceType} recurrenceType plan's recurrence type
   * @returns {(string | null)}
   * @memberof UtilsService
   */
  getPlanRenewEveryText(
    duration: number,
    recurrenceType: PlanRecurrenceType
  ): string | null {
    if (duration && duration > 0) {
      return `${duration} ${this.translationService.instant('DATE_TIME.DAYS')}`;
    } else if (recurrenceType === PlanRecurrenceType.MONTHLY) {
      return this.translationService.instant(
        'GLOBAL_TERMS.PLANS.RECURRENCE_TYPE.MONTHLY'
      );
    }
    return null;
  }

  /**
   * @description The date of the last plan renewal date
   * @param {number} duration
   * @param {PlanRecurrenceType} recurrenceType
   * @param {Date} [startTs=new Date()]
   * @param {Date} endTs
   * @param {number} [renewalCount=0]
   * @returns {(string | null)}
   * @memberof UtilsService
   */
  getPlanLastRenewalDateText(
    duration: number,
    recurrenceType: PlanRecurrenceType,
    startTs: Date = new Date(),
    endTs: Date,
    renewalCount: number = 0,
    subscriptionStatus: SubscriptionStatus = null
  ): string | null {
    const renewalDates = this._getPlanRenewalDates(
      duration,
      recurrenceType,
      startTs,
      endTs,
      renewalCount,
      subscriptionStatus
    );

    if (renewalDates.length > 0) {
      // Filter last renewal dates from today's date
      const lastRenewals = renewalDates.filter((date) =>
        moment().isAfter(date)
      );
      return lastRenewals.length > 0 ? `${lastRenewals[0]}` : null;
    }

    return null;
  }

  private _getPlanRenewalDates(
    duration: number,
    recurrenceType: PlanRecurrenceType,
    startTs: Date = new Date(),
    endTs: Date,
    renewalCount: number = 0,
    subscriptionStatus: SubscriptionStatus = null
  ): string[] {
    const todayDate = moment();
    const monthlyRecurrence = recurrenceType === PlanRecurrenceType.MONTHLY;

    if ((duration && duration > 0) || monthlyRecurrence) {
      const datesLength = moment(endTs).diff(startTs, 'days');
      duration = monthlyRecurrence ? 31 : duration; // 31 = Calendar month
      const periodsCount = Math.floor(datesLength / duration);
      const hasRenewals = periodsCount > 0 || renewalCount > 1;

      if (hasRenewals) {
        const renewalDates: string[] = [];
        const renewals = periodsCount > 0 ? periodsCount : renewalCount;
        let nextRecurrenceDate = moment(startTs);

        // Store all renewal dates in an array
        for (let i = 0; i < renewals; i++) {
          nextRecurrenceDate = monthlyRecurrence
            ? nextRecurrenceDate.add(1, 'months')
            : nextRecurrenceDate.add(duration, 'days');
          renewalDates.push(moment(nextRecurrenceDate).format('YYYY-MM-DD'));
        }

        // Renewal dates list
        return renewalDates;
      }

      // In case where the end date is set to "Unlimited length"
      else if (startTs && !endTs && !renewalCount) {
        const renewalDates: string[] = [];
        let nextRecurrenceDate = moment(startTs);
        let loop = true;

        // Set renewal dates when creating a subscription and on exiting (pre-)active subscriptions
        if (
          [
            SubscriptionStatus.SUBSCRIPTION_PRE_ACTIVE,
            SubscriptionStatus.SUBSCRIPTION_ACTIVE,
          ].includes(subscriptionStatus) ||
          !subscriptionStatus
        ) {
          // Store all renewal dates in an array
          do {
            nextRecurrenceDate = monthlyRecurrence
              ? nextRecurrenceDate.add(1, 'months')
              : nextRecurrenceDate.add(duration, 'days');
            renewalDates.push(moment(nextRecurrenceDate).format('YYYY-MM-DD'));

            if (
              moment(renewalDates[renewalDates.length - 1]).isAfter(todayDate)
            ) {
              loop = false;
            }
          } while (loop);
        }

        // Renewal dates list
        return renewalDates;
      }
    }

    return [];
  }
}
